import type { IconName } from '@fortawesome/fontawesome-common-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ConfigOptionList from '@components/ConfigOptionList/ConfigOptionList';
import SkeletonLoader from '@components/SkeletonLoader';

import { useUpsertAlertRecipientSetting } from '@hooks/api/alerts/alert_recipient_setting';
import { useGetAlertRecipientSetting } from '@hooks/api/alerts/alert_recipient_setting';
import { useSession } from '@hooks/useSession';

import {
  VerifyDeliveryMethod as AlertDeliveryMethod,
  type AlertRecipientType,
} from 'generated/types';

const messages = defineMessages({
  emailTitle: {
    id: 'alert.delivery.email.title',
    defaultMessage: 'Email',
  },
  smsTitle: {
    id: 'alert.delivery.sms.title',
    defaultMessage: 'SMS (Text Message)',
  },
  setup: {
    id: 'alertRecipientSettingForm.setup.title',
    defaultMessage: 'Setup alerts to be sent to your {method}',
  },
  setupWithEmail: {
    id: 'alertRecipientSettingForm.setup.email',
    defaultMessage: 'email',
  },
  setupWithMobile: {
    id: 'alertRecipientSettingForm.setup.mobile',
    defaultMessage: 'mobile device',
  },
  sentTo: {
    id: 'alertRecipientSettingForm.setup.destination',
    defaultMessage: 'Alerts will be sent to {destination}',
  },
  enabled: {
    id: 'alertRecipientSettingForm.enabled',
    defaultMessage: 'when enabled',
  },
});

interface AlertOptionListProps {
  onClick: (value: AlertDeliveryMethod) => void;
  method: AlertDeliveryMethod | null;
  type: AlertRecipientType;
  viewOnly?: boolean;
  /** User or account ID */
  id: string;
}

export default function AlertOptionList({
  id,
  onClick,
  method,
  type,
  viewOnly = false,
}: AlertOptionListProps) {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, loggedInProfile },
  } = useSession();
  const { data: setting, loading } = useGetAlertRecipientSetting(type, id);
  const [enableDisableSetting] = useUpsertAlertRecipientSetting();

  const isEmailConfigured = Boolean(
    setting?.email_destination &&
    setting.email_enabled &&
    setting.email_status === 'approved'
  );
  const isSmsConfigured = Boolean(
    setting?.sms_destination &&
    setting.sms_enabled &&
    setting.sms_status === 'approved'
  );

  const handleToggleChange = async (
    method: AlertDeliveryMethod,
    checked: boolean
  ) => {
    // If not verified and toggle is enabled, open form to
    // prompt user to initiate verification process
    if (
      checked &&
      method === AlertDeliveryMethod.email &&
      setting?.email_status !== 'approved'
    ) {
      onClick(method);
    } else if (
      checked &&
      method === AlertDeliveryMethod.sms &&
      setting?.sms_status !== 'approved'
    ) {
      onClick(method);
    }

    await enableDisableSetting({
      ...(method === AlertDeliveryMethod.email && {
        email_enabled: checked,
      }),
      ...(method === AlertDeliveryMethod.sms && {
        sms_enabled: checked,
      }),
      ...(type === 'account' && {
        account_id: loggedInProfile?.account_id,
      }),
      ...(type === 'user' && {
        user_id: activeProfile?.user_id,
      }),
    });
  };

  const items = [
    {
      disabled: viewOnly,
      key: 'email-alert',
      handleClick: () => {
        onClick(AlertDeliveryMethod.email);
      },
      onToggleChange: (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
      ) => handleToggleChange(AlertDeliveryMethod.email, checked),
      iconName: 'envelope' as IconName,
      isConfigured: isEmailConfigured,
      isEnabled: setting?.email_enabled,
      isSelected: method === AlertDeliveryMethod.email,
      isVerified: setting?.email_status === 'approved',
      showConfigTag: false,
      showVerificationTag: true,
      title: formatMessage(messages.emailTitle),
      subtitle: setting?.email_destination
        ? formatMessage(messages.sentTo, {
            destination: `${setting.email_destination} ${
              setting.email_enabled ? '' : formatMessage(messages.enabled)
            }`,
          })
        : formatMessage(messages.setup, {
            method: formatMessage(messages.setupWithEmail),
          }),
    },
    {
      disabled: viewOnly,
      key: 'sms-alert',
      handleClick: () => {
        onClick(AlertDeliveryMethod.sms);
      },
      onToggleChange: (
        _event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
      ) => handleToggleChange(AlertDeliveryMethod.sms, checked),
      iconName: 'message' as IconName,
      isEnabled: setting?.sms_enabled,
      isConfigured: isSmsConfigured,
      isSelected: method === AlertDeliveryMethod.sms,
      isVerified: setting?.sms_status === 'approved',
      showConfigTag: false,
      showVerificationTag: true,
      title: formatMessage(messages.smsTitle),
      subtitle: setting?.sms_destination
        ? formatMessage(messages.sentTo, {
            destination: `${setting.sms_destination} ${
              setting.sms_enabled ? '' : formatMessage(messages.enabled)
            }`,
          })
        : formatMessage(messages.setup, {
            method: formatMessage(messages.setupWithMobile),
          }),
    },
  ];

  return (
    <SkeletonLoader
      count={items.length}
      height={45}
      loading={loading}
      variant="rounded"
    >
      <ConfigOptionList items={items} />
    </SkeletonLoader>
  );
}
